import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { User } from '../../interface';
import { environment } from '../../../../environments/environment';
import { filterEmpty } from '../../../rxjs';

interface SessionInfo {
  userId: string;
  profileId: string;
  sessionId: string;
}

/**
 * Sign-in state for the dashboard.
 *
 * There is no token here any more. The API runs the OAuth exchange server-side
 * and gives the browser an httpOnly session cookie, so nothing this code can
 * read is worth stealing — which is the point: a cross-site scripting bug used
 * to hand an attacker a long-lived refresh token straight out of storage.
 *
 * The consequence is that we can no longer decide locally whether we are signed
 * in. Reading a JWT's expiry was only ever the browser's guess at what the
 * server would say; now we ask.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private router = inject(Router);
  private httpClient = inject(HttpClient);

  loginStateChange$: Observable<boolean>;

  private loginStateChange = new BehaviorSubject<boolean>(false);
  private user = new BehaviorSubject<User | null>(null);
  private session = new BehaviorSubject<SessionInfo | null>(null);

  user$ = this.user.asObservable();
  authorizedUser$ = this.user$.pipe(filterEmpty());
  userName$ = this.user$.pipe(map(user => this.getFirstName(user)));

  constructor() {
    this.loginStateChange$ = this.loginStateChange.asObservable();
  }

  isLoggedIn(): boolean {
    return this.loginStateChange.value;
  }

  /**
   * Ask the API who we are. Called at start-up, before any guard decides.
   *
   * A 401 is the ordinary answer for a signed-out visitor rather than a
   * failure, so it resolves to false instead of propagating.
   */
  restoreSession(): Observable<boolean> {
    return this.httpClient.get<SessionInfo>(environment.api_url + '/users/session/me', { withCredentials: true }).pipe(
      tap(info => {
        this.session.next(info);
        this.loginStateChange.next(true);
      }),
      map(() => true),
      catchError(() => {
        this.session.next(null);
        this.loginStateChange.next(false);
        return of(false);
      }),
    );
  }

  /**
   * Hand off to the API to begin signing in.
   *
   * A full navigation rather than an XHR: the flow ends in a redirect back from
   * the provider, and the cookie it sets has to arrive on a top-level request.
   */
  login(redirectUrl: string = this.router.url): void {
    const target = redirectUrl.startsWith('/') ? redirectUrl : '/';
    window.location.assign(`${environment.api_url}/users/session/login?redirect=${encodeURIComponent(target)}`);
  }

  logout(): void {
    this.httpClient
      .post(environment.api_url + '/users/session/logout', null, { withCredentials: true })
      .pipe(catchError(() => of(null)))
      .subscribe(() => {
        this.session.next(null);
        this.user.next(null);
        this.loginStateChange.next(false);
        this.router.navigate(['/login']);
      });
  }

  setUser(user: User): void {
    this.user.next(user);
  }

  getUser(): User | null {
    return this.user.value;
  }

  getSession(): SessionInfo | null {
    return this.session.value;
  }

  getUserEmail(): string | null {
    return this.user.value?.email ?? null;
  }

  getFirstName(user = this.user.value): string {
    if (!user || !user.firstName) {
      return 'Stranger';
    }
    return user.firstName;
  }

  getCustomerId(): string | null {
    return this.user.value ? this.user.value.stripeCustomerId : null;
  }

  getSubscriptionId(): string | null {
    return this.user.value ? this.user.value.stripeSubscriptionId : null;
  }
}
